import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AutoAwesome, CheckCircleOutline, ErrorOutline, InfoOutlined } from '@mui/icons-material'
import { Input, Popconfirm, Spin } from 'antd'
import services from '../../services'
import Blocos, { Bloco } from './Blocos'

export interface Mensagem {
  id: number | string
  papel: 'user' | 'assistant'
  blocos: Bloco[]
  sugestoes: string[]
  criadaEm?: string
  /** Só no navegador: avisos e falhas que não ficam gravados na conversa. */
  aviso?: 'erro' | 'info'
}

export interface Saldo {
  usadoPessoa: number
  limitePessoa?: number | null
  usadoAgente: number
  limiteAgente?: number | null
  esgotado: boolean
}

export interface Conversa {
  disponivel: boolean
  legado: boolean
  liberado: boolean
  agente?: string | null
  mensagens: Mensagem[]
  sugestoes: string[]
  saldo?: Saldo | null
  tarefaEmAndamento?: string | null
}

export interface Andamento {
  id: string
  status: 'processando' | 'ok' | 'erro'
  etapas: string[]
  segundos: number
  raciocinio?: string | null
  resposta?: Mensagem | null
  saldo?: Saldo | null
  erro?: string | null
}

const INTERVALO = 1500
const TAMANHO_MAXIMO = 2000

const compacto = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })

const aviso = (texto: string, tipo: 'erro' | 'info' = 'erro'): Mensagem => ({
  id: `aviso-${Date.now()}-${Math.random()}`,
  papel: 'assistant',
  blocos: [{ tipo: 'texto', texto }],
  sugestoes: [],
  aviso: tipo
})

export function CaixaPergunta({ valor, aoMudar, aoEnviar, ocupado, bloqueado, dica }: {
  valor: string
  aoMudar: (v: string) => void
  aoEnviar: () => void
  ocupado?: boolean
  bloqueado?: boolean
  dica?: string
}) {
  return (
    <div className="of-caixa">
      <div className="of-caixa-borda">
        <div className="of-caixa-dentro">
          <Input.TextArea
            value={valor}
            onChange={e => aoMudar(e.target.value)}
            placeholder={dica || 'Pergunte sobre os dados deste dashboard'}
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={TAMANHO_MAXIMO}
            onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); aoEnviar() } }}
            disabled={bloqueado}
            className="of-caixa-texto"
          />
          <div className="of-caixa-acoes">
            {valor.length > TAMANHO_MAXIMO * 0.8 && <span className="of-contador">{valor.length}/{TAMANHO_MAXIMO}</span>}
            <button type="button" className="of-enviar" onClick={aoEnviar} disabled={bloqueado || ocupado || !valor.trim()}>
              <AutoAwesome sx={{ fontSize: 18 }} /> GERAR
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Progresso({ andamento, aoParar }: { andamento: Andamento; aoParar: () => void }) {
  const etapas = andamento.etapas.length ? andamento.etapas : ['Entendendo a pergunta']
  return (
    <div className="of-msg of-msg-ia">
      <AutoAwesome className="of-msg-icone" />
      <div className="of-progresso">
        {etapas.map((e, i) => {
          const atual = i === etapas.length - 1
          return (
            <div key={i} className={`of-etapa ${atual ? 'atual' : 'feita'}`}>
              {atual ? <Spin size="small" /> : <CheckCircleOutline sx={{ fontSize: 16 }} />}
              <span>{e}</span>
            </div>
          )
        })}
        <div className="of-progresso-rodape">
          <span>{andamento.segundos} s</span>
          <button type="button" className="of-link" onClick={aoParar}>Parar de esperar</button>
        </div>
        {andamento.segundos >= 60 && (
          <div className="of-dica">Perguntas que cruzam muitos dados podem levar alguns minutos.</div>
        )}
        {andamento.raciocinio && (
          <details className="of-raciocinio">
            <summary>Raciocínio do agente (só administradores)</summary>
            <pre>{andamento.raciocinio}</pre>
          </details>
        )}
      </div>
    </div>
  )
}

function Consumo({ saldo }: { saldo: Saldo }) {
  const limite = saldo.limitePessoa
  const pct = limite ? Math.min(100, Math.round((saldo.usadoPessoa / limite) * 100)) : null
  return (
    <div className="of-consumo" title="Tokens são a medida de uso da IA. O contador volta a zero no dia 1º.">
      <div className="of-consumo-texto">
        {limite
          ? <>Uso no mês: {compacto.format(saldo.usadoPessoa)} de {compacto.format(limite)} tokens</>
          : <>Uso no mês: {compacto.format(saldo.usadoPessoa)} tokens</>}
      </div>
      {pct !== null && (
        <div className="of-consumo-barra"><div className={pct >= 90 ? 'alto' : ''} style={{ width: `${pct}%` }} /></div>
      )}
    </div>
  )
}

/**
 * A conversa com o Cortex Agent do Snowflake: mensagens, envio, acompanhamento da tarefa em
 * segundo plano e nova conversa. Usada pelo chat do portal antigo e pelo do portal novo.
 */
export function useConversaOneflow(dashId: any, environmentId: any, inicial: Conversa) {
  const [mensagens, setMensagens] = useState<Mensagem[]>(inicial.mensagens || [])
  const [saldo, setSaldo] = useState<Saldo | null | undefined>(inicial.saldo)
  const [pergunta, setPergunta] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [andamento, setAndamento] = useState<Andamento | null>(null)

  const tarefaAtual = useRef<string | null>(null)
  const relogio = useRef<any>(null)
  const fimRef = useRef<HTMLDivElement>(null)

  const ocupado = enviando || andamento !== null
  const esgotado = !!saldo?.esgotado
  const bloqueado = !inicial.liberado || esgotado

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [mensagens, andamento?.etapas.length, enviando])

  const encerrar = useCallback(() => {
    tarefaAtual.current = null
    clearTimeout(relogio.current)
    setAndamento(null)
  }, [])

  const acompanhar = useCallback((id: string) => {
    tarefaAtual.current = id
    setAndamento({ id, status: 'processando', etapas: [], segundos: 0 })
    let falhas = 0

    const passo = async () => {
      if (tarefaAtual.current !== id) return
      const res = await services.api.oneflowTarefa(id)
      if (tarefaAtual.current !== id) return

      if (res.fail) {
        // Sem a tarefa (o Portal reiniciou) não adianta insistir; queda de rede tenta mais um pouco.
        if (res.status === 404 || res.status === 403 || ++falhas >= 5) {
          encerrar()
          setMensagens(m => [...m, aviso(res.error || 'Perdi o andamento dessa pergunta. Envie de novo.')])
          return
        }
        relogio.current = setTimeout(passo, INTERVALO * 2)
        return
      }

      falhas = 0
      const t: Andamento = res.data
      if (t.status === 'processando') {
        setAndamento(t)
        relogio.current = setTimeout(passo, INTERVALO)
        return
      }

      encerrar()
      if (t.saldo) setSaldo(t.saldo)
      if (t.status === 'ok' && t.resposta) {
        const resposta = t.resposta
        setMensagens(m => [...m, resposta])
      } else {
        setMensagens(m => [...m, aviso(t.erro || 'Não consegui concluir essa pergunta agora. Tente de novo.')])
      }
    }

    relogio.current = setTimeout(passo, 800)
  }, [encerrar])

  useEffect(() => {
    if (inicial.tarefaEmAndamento) acompanhar(inicial.tarefaEmAndamento)
    return () => {
      tarefaAtual.current = null
      clearTimeout(relogio.current)
    }
  }, [inicial, acompanhar])

  const enviar = useCallback(async (texto?: string) => {
    const conteudo = (texto ?? pergunta).trim()
    if (!conteudo || ocupado || bloqueado) return

    const local: Mensagem = { id: `local-${Date.now()}`, papel: 'user', blocos: [{ tipo: 'texto', texto: conteudo }], sugestoes: [] }
    setMensagens(m => [...m, local])
    setPergunta('')
    setEnviando(true)

    const res = await services.api.oneflowPerguntar(dashId, environmentId, conteudo)
    setEnviando(false)
    if (res.fail) {
      // Recusada antes de gravar: a pergunta volta para a caixa.
      setMensagens(m => [...m.filter(x => x.id !== local.id), aviso(res.error || 'Não consegui enviar a pergunta.')])
      setPergunta(conteudo)
      return
    }
    acompanhar(res.data.tarefaId)
  }, [pergunta, ocupado, bloqueado, dashId, environmentId, acompanhar])

  const pararDeEsperar = useCallback(() => {
    encerrar()
    setMensagens(m => [...m, aviso('Parei de esperar. Se a resposta ficar pronta, ela aparece quando você abrir o oneflow de novo.', 'info')])
  }, [encerrar])

  const novaConversa = useCallback(async () => {
    const res = await services.api.oneflowNovaConversa(dashId, environmentId)
    if (res.fail) {
      setMensagens(m => [...m, aviso(res.error || 'Não consegui começar uma nova conversa.')])
      return
    }
    setMensagens([])
  }, [dashId, environmentId])

  const ultima = mensagens[mensagens.length - 1]
  const sugestoes = mensagens.length === 0
    ? inicial.sugestoes
    : ultima && ultima.papel === 'assistant' && !ultima.aviso ? ultima.sugestoes : []

  return { mensagens, saldo, pergunta, setPergunta, enviando, andamento, ocupado, esgotado, bloqueado, enviar, pararDeEsperar, novaConversa, sugestoes, fimRef }
}

/** O chat do oneflow com o Cortex Agent do Snowflake. */
export default function ChatOneflow({ dashId, environmentId, inicial }: {
  dashId: any
  environmentId: any
  inicial: Conversa
}) {
  const { mensagens, saldo, pergunta, setPergunta, enviando, andamento, ocupado, esgotado, bloqueado, enviar, pararDeEsperar, novaConversa, sugestoes, fimRef } =
    useConversaOneflow(dashId, environmentId, inicial)

  return (
    <>
      <div className="of-acoes-topo">
        {inicial.agente && <span className="of-agente" title="Agente que responde neste dashboard">{inicial.agente}</span>}
        <Popconfirm title="Começar uma nova conversa?" description="A conversa atual sai da tela." okText="Começar"
                    cancelText="Cancelar" onConfirm={novaConversa} disabled={ocupado || mensagens.length === 0}>
          <button type="button" className="of-link" disabled={ocupado || mensagens.length === 0}>Nova conversa</button>
        </Popconfirm>
      </div>

      <div className="of-mensagens">
        {mensagens.length === 0 && (
          <div className="of-msg of-msg-ia">
            <AutoAwesome className="of-msg-icone" />
            <div className="of-md"><p className="of-md-p">Olá! O que você quer saber sobre este dashboard?</p></div>
          </div>
        )}

        {mensagens.map(m => {
          if (m.papel === 'user') {
            return (
              <div key={m.id} className="of-msg of-msg-pessoa">
                <div className="of-balao">{m.blocos.map(b => b.texto).join('\n')}</div>
              </div>
            )
          }
          if (m.aviso) {
            return (
              <div key={m.id} className={`of-msg of-aviso-msg ${m.aviso}`}>
                {m.aviso === 'erro' ? <ErrorOutline sx={{ fontSize: 18 }} /> : <InfoOutlined sx={{ fontSize: 18 }} />}
                <span>{m.blocos[0]?.texto}</span>
              </div>
            )
          }
          return (
            <div key={m.id} className="of-msg of-msg-ia">
              <AutoAwesome className="of-msg-icone" />
              <div className="of-resposta"><Blocos blocos={m.blocos} /></div>
            </div>
          )
        })}

        {enviando && !andamento && (
          <div className="of-msg of-msg-ia of-linha"><AutoAwesome className="of-msg-icone" /><Spin size="small" /></div>
        )}
        {andamento && <Progresso andamento={andamento} aoParar={pararDeEsperar} />}

        {!ocupado && !bloqueado && sugestoes.length > 0 && (
          <div className="of-sugestoes">
            {sugestoes.map((s, i) => (
              <button key={i} type="button" className="of-sugestao" onClick={() => enviar(s)}>{s}</button>
            ))}
          </div>
        )}
        <div ref={fimRef} />
      </div>

      {!inicial.liberado && (
        <div className="of-faixa">Você ainda não está liberado para usar o oneflow neste dashboard. Peça a um administrador.</div>
      )}
      {inicial.liberado && esgotado && (
        <div className="of-faixa">O limite de uso do oneflow deste mês acabou. Ele volta no dia 1º.</div>
      )}

      <CaixaPergunta valor={pergunta} aoMudar={setPergunta} aoEnviar={() => enviar()} ocupado={ocupado} bloqueado={bloqueado} />

      <div className="of-rodape">
        {saldo && inicial.liberado && <Consumo saldo={saldo} />}
        <span>Criado com IA. É possível cometer erros. Confira os números no dashboard.</span>
      </div>
    </>
  )
}
